import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { ChevronsUp } from 'lucide-preact';
import type { MapValidation, ObjectRef } from '../../sim/map/index.ts';
import { setPanel } from '../panels.ts';
import { EditorIcon } from './icons.tsx';
import {
  EDITOR_LOG_PREVIEW,
  filterLogEntries,
  previewLogEntries,
  type EditorLogEntry,
  type EditorLogFilter,
} from './log.ts';
import './validation.css';

/**
 * The log pane is off by default. A new line fades in, holds for two seconds, then fades out —
 * even while the pane is closed. Log → Show log (or a click on that flash) pins the history.
 * Cleared faults stay in the history, grayed. The pane shows the last three lines; Expand log
 * opens the full list. Test drive lives under Map (and on the tool rail), not this pane.
 */

export const EDITOR_LOG_FLASH_MS = 2500;

export type EditorValidationActions = {
  /** Selects and frames the offending object, so an issue is a jump link and not just a label. */
  onReveal: (ref: ObjectRef) => void;
  /** Pins the log open after a flash, the same as Log → Show log. */
  onShowLog: () => void;
};

export type EditorValidationProps = {
  validation: MapValidation;
  logVisible: boolean;
  logFilter: EditorLogFilter;
  logEntries: readonly EditorLogEntry[];
  logFlashId: number;
  logFlashEntries: readonly EditorLogEntry[];
  actions: EditorValidationActions;
};

export type EditorValidationOptions = {
  getValidation: () => MapValidation;
  getLog: () => {
    visible: boolean;
    filter: EditorLogFilter;
    entries: readonly EditorLogEntry[];
    flashId: number;
    flashEntries: readonly EditorLogEntry[];
  };
  actions: EditorValidationActions;
};

export type EditorValidation = {
  sync: () => void;
  destroy: () => void;
};

function formatLogTime(at: number): string {
  return new Date(at).toLocaleTimeString(undefined, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function levelOf(severity: EditorLogEntry['severity']): string {
  return severity === 'error' ? 'ERROR' : 'WARN';
}

function LogRow({
  entry,
  onReveal,
}: {
  entry: EditorLogEntry;
  onReveal: (ref: ObjectRef) => void;
}): JSX.Element {
  const ref = entry.ref;
  const body = (
    <>
      <span class="editor-log-time">{formatLogTime(entry.at)}</span>
      <span class="editor-log-level">{levelOf(entry.severity)}</span>
      <span class="editor-log-message">{entry.message}</span>
    </>
  );

  return (
    <li
      class="editor-validation-issue editor-log-entry"
      data-severity={entry.severity}
      data-issue={entry.issueId}
      data-resolved={entry.resolved ? 'true' : 'false'}
    >
      {ref ? (
        <button type="button" class="editor-validation-link" onClick={() => onReveal(ref)}>
          {body}
        </button>
      ) : (
        <span class="editor-validation-text">{body}</span>
      )}
    </li>
  );
}

function LogList({
  id,
  entries,
  onReveal,
}: {
  id?: string;
  entries: readonly EditorLogEntry[];
  onReveal: (ref: ObjectRef) => void;
}): JSX.Element {
  return (
    <ul id={id} class="editor-validation-list">
      {entries.length === 0 ? (
        <li class="editor-log-empty">No log entries.</li>
      ) : (
        entries.map((entry) => <LogRow key={entry.id} entry={entry} onReveal={onReveal} />)
      )}
    </ul>
  );
}

function EditorLogModal({
  entries,
  onReveal,
  onClose,
}: {
  entries: readonly EditorLogEntry[];
  onReveal: (ref: ObjectRef) => void;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onClose();
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      class="editor-log-modal-backdrop ui-chrome"
      onClick={onClose}
    >
      <div
        class="editor-log-modal ui-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Editor log"
        onClick={(event) => event.stopPropagation()}
      >
        <div class="editor-log-modal-header">
          <h2 class="editor-log-modal-title">Log</h2>
          <button type="button" class="ui-button" onClick={onClose}>
            Close
          </button>
        </div>
        <LogList
          entries={entries}
          onReveal={(ref) => {
            onClose();
            onReveal(ref);
          }}
        />
      </div>
    </div>
  );
}

export function EditorValidationPanel({
  logVisible,
  logFilter,
  logEntries,
  logFlashId,
  logFlashEntries,
  actions,
}: EditorValidationProps): JSX.Element | null {
  const [flashing, setFlashing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const pinned = filterLogEntries(logEntries, logFilter);
  const preview = previewLogEntries(pinned);
  const showFlash = flashing && !logVisible && logFlashEntries.length > 0;
  const rows = logVisible ? preview : showFlash ? logFlashEntries : [];
  const showList = logVisible || showFlash;
  const canExpand = logVisible && pinned.length > EDITOR_LOG_PREVIEW;

  useEffect(() => {
    if (logFlashId === 0) {
      return;
    }

    setFlashing(true);
    const timer = window.setTimeout(() => setFlashing(false), EDITOR_LOG_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [logFlashId]);

  useEffect(() => {
    if (!canExpand) {
      setExpanded(false);
    }
  }, [canExpand]);

  if (!showList) {
    return null;
  }

  return (
    <>
      <aside
        class="editor-validation ui-panel ui-chrome"
        aria-label="Editor log"
        data-log-open={logVisible ? 'true' : showFlash ? 'flash' : 'false'}
      >
        {canExpand ? (
          <div class="editor-log-chrome">
            <button
              type="button"
              class="editor-log-expand"
              aria-label="Expand log"
              aria-expanded={expanded ? 'true' : 'false'}
              onClick={() => setExpanded(true)}
            >
              <EditorIcon icon={ChevronsUp} />
            </button>
          </div>
        ) : null}
        <ul
          key={showFlash ? logFlashId : 'pinned'}
          id="editor-validation-list"
          class={`editor-validation-list${showFlash ? ' is-flash' : ''}`}
          hidden={!showList}
          onClick={() => {
            if (showFlash) {
              actions.onShowLog();
            }
          }}
        >
          {rows.length === 0 ? (
            <li class="editor-log-empty">No log entries.</li>
          ) : (
            rows.map((entry) => (
              <LogRow key={entry.id} entry={entry} onReveal={actions.onReveal} />
            ))
          )}
        </ul>
      </aside>
      {expanded ? (
        <EditorLogModal
          entries={pinned}
          onReveal={actions.onReveal}
          onClose={() => setExpanded(false)}
        />
      ) : null}
    </>
  );
}

export function mountEditorValidation(options: EditorValidationOptions): EditorValidation {
  const push = (): void => {
    const log = options.getLog();
    setPanel('editorValidation', {
      validation: options.getValidation(),
      logVisible: log.visible,
      logFilter: log.filter,
      logEntries: log.entries,
      logFlashId: log.flashId,
      logFlashEntries: log.flashEntries,
      actions: options.actions,
    });
  };

  push();

  return {
    sync: push,
    destroy: () => setPanel('editorValidation', null),
  };
}
